import * as T from '../Types';
import * as E from '../Enums';

export class Order {

    /** The most recent price (setter triggers gainPercentage recalculation) */
    private _lastPrice: number | null = null;

    get lastPrice(): number | null {
        return this._lastPrice;
    }

    set lastPrice(price: number | null) {
        this._lastPrice = price;
        // Automatically recalculate gain values when price is set
        if (price !== null) {
            this.calculateGainPercentage(price);
        }
    }
    
    constructor(
        /** Internal order ID */
        public id: string,
        
        /** The current status of the order */
        public status: E.OrderStatus,
        /** The financial instrument associated with the order */
        public instrument: T.Instrument,
        /** The side of the order (buy or sell) */
        public side: E.OrderSide,
        /** Market or Flex */
        public type: E.OrderType,
        /** The number of contracts */
        public quantity: number,
        /** Day or Good-Til-Canceled */
        public timeInForce: E.TimeInForce,
        /** When the order was created internally */
        public createdAt: string,
        
        /** The order's PnL stats */
        public stats?: {
            /** The average PER-SHARE cost per unit. Does not change if position is added
             * to after this order has been filled. */
            unitCost: number,
            /** The current gain in value of this order */
            gainValue?: number,
            /** The current gain percentage of this order */
            gainPercentage?: number,
            /** The quantity of the order that has been filled */
            filledQuantity?: number,
            /** The average price at which the order has been filled */
            avgFillPrice?: number,
            /** The slippage incurred during order execution */
            slippage?: number,
        },

        /** Take-profit price trigger */
        public takeProfit?: T.Trigger,
        /** Stop-loss price trigger */
        public stopLoss?: T.Trigger,
        /** The distance by which to trail the stop-loss, using the stopLoss trigger's reference */
        public trailDistance?: number,
        /** When the order was triggered and submitted to the broker */
        public triggeredAt?: string,

        /** Error message if the order encountered an issue */
        public error?: string
    ) {}


    /** Check if the order is in a terminal state (cancelled, expired, filled, or rejected) */
    isTerminal(): boolean {
        if ([
            E.OrderStatus.CANCELLED,
            E.OrderStatus.EXPIRED,
            E.OrderStatus.FILLED,
            E.OrderStatus.REJECTED
            ].includes(this.status)
        ) {
            return true;
        }
        return false;
    }


    /**
     * Calculates and updates the gainPercentage and gainValue based on current contract price.
     */
    calculateGainPercentage(currentPrice: number) {
        if (this.stats) {
            this.stats.gainValue = (currentPrice - this.stats.unitCost) * this.quantity * 100;
            this.stats.gainPercentage = ((currentPrice - this.stats.unitCost) / this.stats.unitCost) * 100;
        }
    }
}